import Vue from 'vue'
import Router from 'vue-router'
import AuthenticatedRoutes from './authenticated'
import AdvisoryRoutes, { AdvisoryRedirect } from './advisory'
import Cookie from '@/utils/cookie'
import { featureFlag } from '@/../app-config.js'
import store from '@/store'

Vue.use(Router)

const pages = require('./pages').default
const authenticatedRoutes = AuthenticatedRoutes(pages)
const advisoryRoutes = AdvisoryRoutes(pages, authenticatedRoutes)
const advisoryRedirect = AdvisoryRedirect(authenticatedRoutes)

const router = new Router({
  mode: 'history',
  linkActiveClass: 'active-link',
  scrollBehavior: function (to, from, savedPosition) {
    // scroll to anchor by returning the selector
    if (to.hash) {
      // bypass #anchor check
      if (/^#\d/.test(to.hash) || document.querySelector(to.hash)) {
        const el = document.querySelector(to.hash)
        el.scrollIntoView()

        return { selector: to.hash }
      }
    }

    if (document.getElementById('router-container')) {
      document.getElementById('router-container').scrollTop = 0
    }
    return { x: 0, y: 0 }
  },

  routes: [
    {
      path: '/',
      name: 'landing-page',
      redirect: {
        name: 'authenticated'
      }
    },
    {
      path: '/cgu',
      name: 'cgu-container',
      component: pages.lobbyContainer,
      redirect: {
        name: 'cgu'
      },
      children: [
        {
          path: '',
          name: 'cgu',
          component: pages.cguPage
        }
      ]
    },
    {
      path: '/maintenance',
      name: 'maintenance',
      component: pages.maintenance
    },
    {
      path: '/loading',
      name: 'loading',
      component: pages.loading
    },
    {
      path: '/delete-profile',
      name: 'delete-profile',
      component: pages.deleteProfile
    },
    {
      path: '/bi-terms',
      name: 'bi-terms',
      component: pages.accountsBITerms,
      props: true
    },
    {
      path: '/app',
      name: 'authenticated',
      component: pages.authenticatedPage,
      meta: {
        authenticated: true
      },
      redirect: {
        name: 'dashboard'
      },
      children: authenticatedRoutes
    },
    {
      path: '/app',
      component: pages.lobbyContainer,
      meta: {
        authenticated: true
      },
      children: [
        {
          path: 'accounts/add',
          component: pages.accountsAdd,
          children: [
            {
              path: '',
              name: 'accounts-add-index',
              component: pages.accountsAddIndex,
              meta: {
                hideMenu: true
              }
            },
            {
              path: ':bankId',
              name: 'accounts-add-form',
              component: pages.accountsAddForm,
              props: true,
              meta: {
                hideMenu: true
              }
            }
          ]
        },
        {
          path: 'load',
          name: 'accounts-load',
          component: pages.accountsLoad,
          props: true
        },
        {
          path: 'profile',
          name: 'profile-fill',
          component: pages.profileFill,
          redirect: { name: 'profile-fill-risk' },
          children: [
            {
              path: 'risk',
              name: 'profile-fill-risk',
              component: pages.profileFillRisk
            },
            {
              path: 'infos',
              name: 'profile-fill-infos',
              component: pages.profileFillInfos,
              props: true
            },
            {
              path: 'result',
              name: 'profile-result',
              component: pages.profileResult
            }
          ]
        },
        {
          path: 'accounts/risk',
          name: 'accounts-risk',
          component: pages.accountsRisk
        },
        {
          path: 'confirmation',
          name: 'confirmation',
          component: pages.confirmationPage
        },
        {
          path: 'subscribe',
          name: 'subscribe',
          component: pages.subscriptionPage
        },
        {
          path: 'pay',
          name: 'pay',
          component: pages.paymentPage,
          props: true
        }
      ]
    },
    {
      path: '/app',
      name: 'unauthenticated',
      component: pages.lobbyContainer,
      meta: {
        authenticated: false
      },
      redirect: {
        name: 'login'
      },
      children: [
        {
          path: 'login',
          name: 'login',
          component: pages.loginPage,
          meta: { authentication: true }
        },
        {
          path: 'register',
          name: 'register',
          component: pages.registerPage,
          meta: { authentication: true }
        },
        {
          path: 'password-reset',
          name: 'password-reset',
          component: pages.resetPasswordPage,
          meta: { authentication: true }
        },
        {
          path: 'upgrade-account-features',
          name: 'upgrade-account-features',
          component: pages.upgradeAccountFeatures,
          meta: { authentication: false }
        },
        {
          path: 'validation',
          name: 'validation',
          component: pages.validationPage,
          meta: { authentication: false, otp: true }
        }
      ]
    },
    {
      path: '/auth/exchange',
      name: 'keycloak-callback',
      component: pages.loginKeycloakCallbackPage,
      props: true,
      meta: { authentication: false }
    },
    {
      path: '/advisory',
      name: 'advisory-unauthenticated',
      component: pages.lobbyContainer,
      meta: {
        advisory: true,
        authenticated: false
      },
      redirect: {
        name: 'advisory-login'
      },
      children: [
        {
          path: 'login',
          name: 'advisory-login',
          component: pages.advisoryLoginPage,
          redirect: {
            name: 'login'
          }
        }
      ]
    },
    advisoryRoutes,
    {
      path: '/500',
      name: 'error500',
      component: pages.error500
    },
    {
      path: '/404',
      name: 'error404',
      component: pages.error404
    },
    {
      path: '*',
      redirect: {
        name: 'error404'
      }
    }
  ]
})

router.beforeEach(async (to, from, next) => {
  const { matched } = to

  if (to.meta.sidebarSection) {
    store.commit('ux/setSidebarSection', to.meta.sidebarSection)
  }

  // Auth
  const authenticatedCustomer = Cookie.get('hasRole') === 'customer'
  const authenticatedAdvisor = Cookie.get('hasRole') === 'advisor'
  const authenticated = Boolean(Cookie.get('accessToken'))
  const unconfirmedUser = authenticated && Cookie.get('hasRole') === 'unconfirmed'

  // Features
  const maintenanceMode = featureFlag('enableMaintenancePage')
  const conseilEnabled = featureFlag('enableConseil')
  const aClientViewEnabled = featureFlag('aClientView')
  const aInviteClientsEnabled = featureFlag('aInviteClients')
  const aInteractions = featureFlag('aInteractions')
  const allowUserRegistration = featureFlag('allowUserRegistration')

  if (maintenanceMode) {
    if (to.name === 'maintenance') {
      return next()
    } else {
      return next({ name: 'maintenance' })
    }
  }

  if (!conseilEnabled) {
    // If Conseil is disabled disallow access to other areas of platform
    if (!authenticatedAdvisor) {
      if (to.name === 'password-reset') {
        return next()
      }
      if (matched[0].path !== '/advisory') {
        return next({ name: 'advisory-login' })
      }
    }
    if (authenticatedAdvisor && matched[0].path !== '/advisory') {
      return next({ name: 'advisory-authenticated' })
    }
  }

  if (!aClientViewEnabled) {
    // If Client view is disabled disallow access to feature routes
    const regex = /(\/advisory\/customers\/)\d+(\/view\/).*/
    if (regex.test(to.fullPath)) {
      return next({ name: 'advisory-authenticated' })
    }
  }

  if (!aInviteClientsEnabled) {
    if (to.name === 'customers-add') {
      return next({ name: 'advisory-authenticated' })
    }
  }

  if (!allowUserRegistration) {
    if (to.name === 'register') {
      return next({ name: 'login' })
    }
  }

  if (!aInteractions) {
    if (to.name === 'customer-interactions') {
      return next({ name: 'advisory-authenticated' })
    }
  }

  if (to.name === 'accounts-load' && from.name !== 'accounts-add-form') {
    // Redirects to dashboard/accounts-add page if going back to accounts-load page
    return next({
      name: 'authenticated'
    })
  }

  if (authenticatedAdvisor && advisoryRedirect.indexOf(to.name) >= 0) {
    return next({
      name: `advisor-${to.name}`,
      params: to.params,
      hash: to.hash,
      query: to.query
    })
  }

  // If route needs credentials and the user has none do a redirect to login
  if (!authenticated) {
    if (matched[0].path === '/advisory' || matched[0].path === '/app') {
      if (!to.meta.authentication && !to.meta.otp) {
        return next({
          name: 'login',
          query: {
            redirect: to.fullPath
          }
        })
      }
    }
  }

  if (authenticated) {
    // Block unconfirmed users from accessing the platform
    if (unconfirmedUser && to.name !== 'confirmation') {
      return next({ name: 'confirmation' })
    }

    await store.dispatch('auth/fetchUser')

    const isEsrSsoRestriction = store.getters.isEsrSsoRestriction
    const cguValidated = store.getters['auth/isCguValidated']

    if (authenticatedCustomer && !isEsrSsoRestriction && !cguValidated && to.name !== 'cgu') {
      return next({ name: 'cgu' })
    }

    // Redirect homepage to user role homepage
    if (to.path === '/') {
      if (authenticatedCustomer) {
        return next({ name: 'authenticated' })
      }
      if (authenticatedAdvisor) {
        return next({ name: 'advisory-authenticated' })
      }
    }

    // Disallow access to login if user is authenticated
    if (to.meta.authentication) {
      return next({ path: '/' })
    }

    // Disallow access to other areas of website for respective roles
    if (matched[0].path === '/advisory' && authenticatedCustomer) {
      return next({ path: '/' })
    }

    if (matched[0].path === '/app' && authenticatedAdvisor) {
      return next({ name: '/' })
    }
  }

  next()
})

export default router
